import UserComplianceTracker from "@/components/dashboard/UserComplianceTracker";
import EmailForm from "@/components/EmailForm";
import EmailList from "@/components/EmailList";
import ReminderPreferencesPanel from "@/components/ReminderPreferencesPanel";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import EmptyState from "@/components/ui/empty-state";
import KPICard from "@/components/ui/kpi-card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import WeeklyReportEmailTemplate from "@/components/WeeklyReportEmailTemplate";
import { DraftList } from "@/components/DraftCard";
import { useAuth } from "@/contexts/AuthContext";
import { useReports } from "@/hooks/use-reports";
import { useDraftReports } from "@/hooks/useDraftStatus";

import { emailService } from "@/services/emailService";
import { reportService } from "@/services/reportService";
import { storage } from "@/services/storage";
import type { WeeklyReport } from "@/types/WeeklyReport";
import { format, isThisWeek, subWeeks, isValid, parseISO } from "date-fns";
import {
  AlertTriangle,
  BarChart3,
  Bell,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  PlusCircle,
  Send,
  TrendingUp,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// Custom toast functions with colors
const showSuccessToast = (message: string) => {
  toast.success(message);
};

const showInfoToast = (message: string) => {
  toast.info(message);
};

const showErrorToast = (message: string) => {
  toast.error(message);
};

// Error component to display errors
const ErrorDisplay = ({
  error,
  onRetry,
}: {
  error: Error;
  onRetry: () => void;
}) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
      <AlertTriangle className="h-8 w-8 text-destructive" />
    </div>
    <h2 className="text-xl font-semibold text-foreground mb-2">
      Something went wrong
    </h2>
    <p className="text-muted-foreground mb-6 max-w-md">{error.message}</p>
    <Button onClick={onRetry}>Try Again</Button>
  </div>
);

// Helper to generate correlation IDs for structured logging
const generateCorrelationId = (): string =>
  `cid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Logger wrapper for structured logging with context
const logger = {
  group: (name: string, fn: () => void) => {
    console.group(`[Dashboard] ${name}`);
    try {
      fn();
    } finally {
      console.groupEnd();
    }
  },
  info: (message: string, context?: Record<string, any>) => {
    console.log(
      `[Dashboard] ${message}`,
      context ? JSON.stringify(context, null, 2) : "",
    );
  },
  error: (message: string, error?: Error | unknown) => {
    console.error(`[Dashboard] ${message}`, error);
  },
};

const Dashboard = () => {
  const { user, hasGmailAccess } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState<WeeklyReport[]>(() => {
    const existingReportsStr = storage.getItem("weeklyReports");
    return existingReportsStr ? JSON.parse(existingReportsStr) : [];
  });
  const [showReportForm, setShowReportForm] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = storage.getItem("dashboard_active_tab");
    return savedTab || "overview";
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [editingReport, setEditingReport] = useState<WeeklyReport | null>(null);
  const [resumingDraftId, setResumingDraftId] = useState<string | null>(null);

  const [previewContent, setPreviewContent] = useState<string>("");

  // State to track pending edit from URL parameter
  const [pendingEditId, setPendingEditId] = useState<string | null>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("edit");
  });

  // Get draft reports using the hook (filters reports by draft status)
  const { drafts } = useDraftReports(reports);
  
  // Debug: Log drafts data
  useEffect(() => {
    if (drafts.length > 0) {
      console.log("🔵 Dashboard: Drafts available:", drafts.map(d => ({
        id: d.id,
        subject: d.subject,
        hasLastWeekTasks: d.last_week_tasks?.length || 0,
        hasUnplanned: d.Unplanned?.length || 0,
      })));
    }
  }, [drafts]);

  // Process pending edit from URL parameter after reports are loaded
  useEffect(() => {
    if (pendingEditId && reports.length > 0 && !loading) {
      const reportToEdit = reports.find(r => r.id === pendingEditId);
      if (reportToEdit) {
        console.log("🔵 Dashboard: Found report to edit from URL:", {
          id: reportToEdit.id,
          subject: reportToEdit.subject,
          hasLastWeekTasks: reportToEdit.last_week_tasks?.length || 0,
        });
        setEditingReport(reportToEdit);
        setPendingEditId(null); // Clear pending edit
      } else {
        console.warn("🔴 Dashboard: Report not found for edit ID:", pendingEditId);
        setPendingEditId(null);
      }
    }
  }, [pendingEditId, reports, loading]);

  // Calculate KPI metrics
  const kpiMetrics = useMemo(() => {
    const totalReports = reports.length;
    const sentReports = reports.filter((r) => r.sentByEmail).length;

    const thisWeekReports = reports.filter((r) => {
      try {
        return isThisWeek(new Date(r.date));
      } catch {
        return false;
      }
    }).length;

    // Calculate compliance rate (sent vs total this week)
    const complianceRate =
      thisWeekReports > 0
        ? Math.round(
            (reports.filter(
              (r) => r.sentByEmail && isThisWeek(new Date(r.date)),
            ).length /
              1) *
              100,
          )
        : 0;

    return {
      totalReports,
      sentReports,

      thisWeekReports,
      complianceRate: Math.min(complianceRate, 100),
    };
  }, [reports]);

  // Recent activity
  const recentActivity = useMemo(() => {
    return [...reports]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 5);
  }, [reports]);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }

    // Check for query parameters in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get("tab");
    const dateRangeParam = urlParams.get("dateRange");
    const editParam = urlParams.get("edit");

    // Set active tab from URL parameter if available
    if (
      tabParam &&
      ["overview", "create", "history", "compliance", "reminders"].includes(
        tabParam,
      )
    ) {
      setActiveTab(tabParam);

      // Store date range if provided
      if (dateRangeParam && tabParam === "create") {
        storage.setItem("report_date_range", dateRangeParam);
      }
      
      // Store edit ID if provided (will be processed after reports load)
      if (editParam) {
        setPendingEditId(editParam);
        console.log("🔵 Dashboard: URL edit param detected:", editParam);
      }

      // Clean up the URL
      window.history.replaceState({}, document.title, "/dashboard");
    }
    // If no URL parameters, check localStorage
    else {
      const savedTab = storage.getItem("dashboard_active_tab");
      if (savedTab) {
        setActiveTab(
          savedTab as "overview" | "create" | "reports" | "compliance",
        );
        storage.removeItem("dashboard_active_tab");
      }
    }

    const initializeDashboard = async () => {
      const correlationId = generateCorrelationId();
      logger.info("Starting dashboard initialization", {
        correlationId,
        userId: user?.email || "unknown",
        timestamp: new Date().toISOString(),
      });
      try {
        setLoading(true);
        setError(null);

        // Fetch reports using reportService
        const fetchReports = async () => {
          try {
            const fetchedReports = await reportService.getReports(
              user?.email || "",
            );

            // Filter reports for the current user with null check
            const userReports = fetchedReports.filter(
              (report) => report.createdBy === (user?.email || "unknown"),
            );

            // Merge with existing localStorage reports for backward compatibility
            const existingReportsStr = storage.getItem("weeklyReports");
            const existingReports = existingReportsStr
              ? JSON.parse(existingReportsStr)
              : [];

            // Combine and deduplicate reports
            const allReports = [...fetchedReports, ...existingReports];
            const uniqueReports = allReports.filter(
              (report, index, self) =>
                index === self.findIndex((r) => r.id === report.id),
            );

            // Store combined reports in localStorage for backward compatibility
            storage.setItem("weeklyReports", JSON.stringify(allReports));

            // Filter reports for the current user
            const filteredReports = uniqueReports.filter(
              (report) => report.createdBy === (user?.email || "unknown"),
            );

            storage.setItem("weeklyReports", JSON.stringify(filteredReports));

            // Normalize report task arrays to the new structure { value, dependency? }
            const normalizedReports = filteredReports.map((report) => {
              const normalizeTaskArray = (
                tasks: any[] | undefined,
              ): { value: string; dependency?: string }[] => {
                if (!tasks) return [];
                return tasks
                  .map((task) =>
                    typeof task === "string"
                      ? { value: task, dependency: "" }
                      : {
                          value: task?.value || "",
                          dependency: task?.dependency || "",
                        },
                  )
                  .filter((task) => task.value.trim());
              };

              return {
                ...report,
                last_week_tasks: normalizeTaskArray(report.last_week_tasks),
                Unplanned: normalizeTaskArray(report.Unplanned),
                blockers: normalizeTaskArray(report.blockers),
                next_week_tasks: normalizeTaskArray(report.next_week_tasks),
                further_beyond: normalizeTaskArray(report.further_beyond),
              };
            });

            // Sort NORMALIZED reports by date (newest first)
            const sortedReports = [...normalizedReports].sort((a, b) => {
              const dateA = new Date(a.date || 0);
              const dateB = new Date(b.date || 0);
              return dateB.getTime() - dateA.getTime();
            });

            setReports(sortedReports);
          } catch (error) {
            console.error("Error fetching reports:", error);
            toast.error("Failed to load reports. Trying with local data...");

            // Try loading reports from localStorage as a fallback
            try {
              const existingReportsStr = localStorage.getItem("weeklyReports");
              if (existingReportsStr) {
                const allReports = JSON.parse(existingReportsStr);
                const userReports = allReports.filter(
                  (report: WeeklyReport) =>
                    report.createdBy === (user?.email || "unknown"),
                );
                setReports(userReports);
              }
            } catch (localError) {
              console.error("Error loading from localStorage:", localError);
            }
          }
        };

        await fetchReports();
      } catch (error) {
        console.error("Error initializing dashboard:", error);
        setError(
          error instanceof Error ? error : new Error("Failed to load reports"),
        );
        toast.error("Failed to load reports. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    initializeDashboard();
  }, [user, navigate]);

  useEffect(() => {
    storage.setItem("dashboard_active_tab", activeTab);
  }, [activeTab]);

  const handleSendReport = async (processedReport: any) => {
    const correlationId = generateCorrelationId();
    logger.group(`handleSendReport [${correlationId}]`, () => {
      logger.info("Send report triggered", {
        correlationId,
        reportId: processedReport.id,
        userId: user?.email || "unknown",
        subject: processedReport.subject,
        timestamp: new Date().toISOString(),
      });
    });
    try {
      // Convert dateRange to string if it's an object
      const dateRangeStr =
        typeof processedReport.dateRange === "object"
          ? `${format(processedReport.dateRange.from, "MMM d, yyyy")} - ${format(processedReport.dateRange.to, "MMM d, yyyy")}`
          : processedReport.dateRange;

      // Create report object with creator info
      const reportWithCreator = {
        ...processedReport,
        id: editingReport?.id || processedReport.id || Date.now().toString(),
        dateRange: dateRangeStr,
        date: editingReport?.date || new Date().toISOString(),
        createdBy: user?.email || processedReport.createdBy || "User",
        to: Array.isArray(processedReport.to) ? processedReport.to : [],
        cc: Array.isArray(processedReport.cc) ? processedReport.cc : [],
        bcc: Array.isArray(processedReport.bcc) ? processedReport.bcc : [],
      };

      // Save report using reportService
      // Note: Email is already sent by EmailForm.tsx when shouldSend is true
      // We just need to save the report once with the correct sentByEmail flag
      try {
        await reportService.saveReport(reportWithCreator);

        // Refresh the report list
        const updatedReports = await reportService.getReports(
          user?.email || "",
        );
        const userReports = updatedReports.filter(
          (report) => report.createdBy === (user?.email || "unknown"),
        );
        setReports(userReports);

        // Update editingReport with the saved report data so the form refreshes
        if (editingReport) {
          setEditingReport(reportWithCreator);
        }
      } catch (error) {
        console.error("Error saving report:", error);
        showErrorToast("Failed to save report");
        throw error;
      }

      // Check if user came from compliance tab
      const fromComplianceTab =
        storage.getItem("from_compliance_tab") === "true";
      if (fromComplianceTab) {
        setActiveTab("compliance");
        storage.removeItem("from_compliance_tab");
      }

      // Check for stored date range
      const storedDateRange = storage.getItem("report_date_range");
      if (storedDateRange) {
        storage.removeItem("report_date_range");
      }
    } catch (error) {
      console.error("Error in handleSendReport:", error);
      throw error;
    }
  };

  const handleResumeDraft = async (report: WeeklyReport) => {
    console.log("🔵 RESUME DRAFT: handleResumeDraft called with:", {
      id: report.id,
      subject: report.subject,
      hasLastWeekTasks: report.last_week_tasks?.length || 0,
      hasUnplanned: report.Unplanned?.length || 0,
    });
    
    setResumingDraftId(report.id);
    try {
      // For drafts, we can directly edit them
      await handleEditReport(report);
    } finally {
      setResumingDraftId(null);
    }
  };

  const handleDeleteDraft = async (report: WeeklyReport) => {
    try {
      await reportService.deleteReport(report.id);
      // Refresh reports
      const updatedReports = await reportService.getReports(user?.email || "");
      const userReports = updatedReports.filter(
        (r) => r.createdBy === (user?.email || "unknown"),
      );
      setReports(userReports);
      showSuccessToast("Draft deleted successfully");
    } catch (error) {
      console.error("Error deleting draft:", error);
      showErrorToast("Failed to delete draft");
    }
  };

  const handleEditReport = async (report: WeeklyReport) => {
    const correlationId = generateCorrelationId();
    logger.group(`handleEditReport [${correlationId}]`, () => {
      logger.info("Edit report triggered", {
        correlationId,
        reportId: report.id,
        userId: user?.email || "unknown",
        reportSubject: report.subject,
        timestamp: new Date().toISOString(),
      });
    });
    
    console.log("🔵 RESUME DRAFT: handleEditReport called with report:", {
      reportId: report.id,
      reportSubject: report.subject,
      hasLastWeekTasks: report.last_week_tasks?.length || 0,
      hasUnplanned: report.Unplanned?.length || 0,
      sentByEmail: report.sentByEmail,
      status: report.status,
    });

    // Directly load report without draft service
    setEditingReport(report);
    setActiveTab("create");
    
    console.log("🔵 RESUME DRAFT: State updated - editingReport set, activeTab='create'");
  };

  const handlePreview = (reportData: any) => {
    try {
      const template = WeeklyReportEmailTemplate({
        name: user?.name || user?.email || "User",
        dateRange: reportData.dateRange,
        last_week_tasks: reportData.last_week_tasks || [],
        Unplanned: reportData.Unplanned || [],
        blockers: reportData.blockers || [],
        next_week_tasks: reportData.next_week_tasks || [],
        further_beyond: reportData.further_beyond || [],
        notes: reportData.notes || "",
        to: Array.isArray(reportData.to)
          ? reportData.to.join(", ")
          : reportData.to || "",
        cc: Array.isArray(reportData.cc)
          ? reportData.cc.join(", ")
          : reportData.cc || "",
        bcc: Array.isArray(reportData.bcc)
          ? reportData.bcc.join(", ")
          : reportData.bcc || "",
      });

      setPreviewContent(template);
    } catch (error) {
      console.error("Error generating preview:", error);
      toast.error("Failed to generate email preview");
    }
  };

  // Get the most recent report to use for pre-filling the next report
  const lastReport =
    reports.length > 0
      ? [...reports].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        )[0]
      : undefined;

  // Sort reports by date (newest first)
  const sortedReports = [...reports].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  // Format for default subject
  const getDefaultSubject = () => {
    const storedDateRange = localStorage.getItem("report_date_range");
    if (storedDateRange) {
      localStorage.removeItem("report_date_range");
      return `Develeap - Weekly Report - ${storedDateRange}`;
    }

    const today = new Date();
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (5 - today.getDay()));

    return `Develeap - Weekly Report - ${format(today, "MMM d")} to ${format(endOfWeek, "MMM d, yyyy")}`;
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    storage.setItem("dashboard_active_tab", value);
  };

  if (error) {
    return (
      <ErrorDisplay error={error} onRetry={() => window.location.reload()} />
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            Welcome back, {user?.name?.split(" ")[0] || "User"}
          </h1>
          <p className="text-muted-foreground mt-1">
            Here&apos;s an overview of your weekly reporting activity
          </p>
        </div>
        <Button
          onClick={async () => {
            // Check Gmail access before allowing new report creation
            if (!hasGmailAccess) {
              showErrorToast(
                "Please authenticate with Google first to send emails",
              );

              // User will see auth warning in the form itself
            }
            setEditingReport(null);
            setActiveTab("create");
          }}
          size="lg"
          className="gap-2"
        >
          <PlusCircle className="h-5 w-5" />
          New Report
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Reports"
          value={kpiMetrics.totalReports}
          subtitle="All time"
          icon={FileText}
          loading={loading}
        />
        <KPICard
          title="Sent Reports"
          value={kpiMetrics.sentReports}
          subtitle="Emails sent successfully"
          icon={Send}
          loading={loading}
        />
        <KPICard
          title="This Week"
          value={kpiMetrics.thisWeekReports}
          subtitle="Reports created"
          icon={Clock}
          loading={loading}
        />
        <KPICard
          title="Compliance"
          value={`${kpiMetrics.complianceRate}%`}
          subtitle="Weekly target"
          icon={CheckCircle2}
          loading={loading}
        />
      </div>

      {/* Main Content Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <CardHeader className="pb-0">
            <TabsList className="w-full sm:w-auto">
              <TabsTrigger value="overview" className="gap-2">
                <BarChart3 className="h-4 w-4" />
                <span className="hidden sm:inline">Overview</span>
              </TabsTrigger>
              <TabsTrigger value="create" className="gap-2">
                <PlusCircle className="h-4 w-4" />
                <span className="hidden sm:inline">Create</span>
              </TabsTrigger>
              <TabsTrigger value="history" className="gap-2">
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">History</span>
              </TabsTrigger>
              <TabsTrigger value="compliance" className="gap-2">
                <CheckCircle2 className="h-4 w-4" />
                <span className="hidden sm:inline">Compliance</span>
              </TabsTrigger>
              <TabsTrigger value="reminders" className="gap-2">
                <Bell className="h-4 w-4" />
                <span className="hidden sm:inline">Reminders</span>
              </TabsTrigger>
            </TabsList>
          </CardHeader>

          <CardContent className="pt-6">
            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-0 space-y-6">
              {loading ? (
                <div className="space-y-4">
                  <Skeleton className="h-8 w-48" />
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                </div>
              ) : recentActivity.length === 0 ? (
                <EmptyState
                  icon={FileText}
                  title="No reports yet"
                  description="Create your first weekly report to get started with tracking your progress."
                  action={{
                    label: "Create Report",
                    onClick: () => setActiveTab("create"),
                  }}
                />
              ) : (
                <>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      Recent Activity
                    </h3>
                    <div className="space-y-3">
                      {recentActivity.map((report, index) => (
                        <div
                          key={report.id}
                          className="flex items-center justify-between p-4 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer animate-slide-up"
                          style={{ animationDelay: `${index * 50}ms` }}
                          onClick={() => handleEditReport(report)}
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                report.sentByEmail
                                  ? "bg-success/10 text-success"
                                  : "bg-warning/10 text-warning"
                              }`}
                            >
                              {report.sentByEmail ? (
                                <CheckCircle2 className="h-5 w-5" />
                              ) : (
                                <Clock className="h-5 w-5" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-foreground">
                                {report.subject || "Untitled Report"}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {(() => {
                                  try {
                                    if (!report.date) return "No date";
                                    const date =
                                      typeof report.date === "string"
                                        ? parseISO(report.date)
                                        : new Date(report.date);
                                    return isValid(date)
                                      ? format(date, "MMM d, yyyy")
                                      : "Invalid date";
                                  } catch {
                                    return "Invalid date";
                                  }
                                })()}
                              </p>
                            </div>
                          </div>
                          <StatusBadge
                            status={
                              report.status ||
                              (report.sentByEmail ? "sent" : "draft")
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Drafts Section */}
                  <DraftList
                    drafts={drafts}
                    onResume={handleResumeDraft}
                    onDelete={handleDeleteDraft}
                    onCreateNew={() => setActiveTab("create")}
                    isResuming={resumingDraftId}
                  />

                  {/* Quick stats */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
                    <div className="p-4 rounded-lg bg-secondary/30">
                      <div className="flex items-center gap-2 text-muted-foreground mb-2">
                        <TrendingUp className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          Report Trend
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-foreground">
                        {
                          reports.filter((r) => {
                            try {
                              const date = new Date(r.date);
                              const fourWeeksAgo = subWeeks(new Date(), 4);
                              return date >= fourWeeksAgo;
                            } catch {
                              return false;
                            }
                          }).length
                        }
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Reports in last 4 weeks
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary/30">
                      <div className="flex items-center gap-2 text-muted-foreground mb-2">
                        <Send className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          Delivery Rate
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-foreground">
                        {kpiMetrics.totalReports > 0
                          ? Math.round(
                              (kpiMetrics.sentReports /
                                kpiMetrics.totalReports) *
                                100,
                            )
                          : 0}
                        %
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Reports sent vs created
                      </p>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Create Tab */}
            <TabsContent value="create" className="mt-0">
              <EmailForm
                key={editingReport?.id || 'new-report'}
                onSubmit={handleSendReport}
                lastReport={editingReport}
                onPreview={handlePreview}
                previewContent={previewContent}
                reports={sortedReports}
                defaultSubject={getDefaultSubject()}
              />
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history" className="mt-0">
              <EmailList
                reports={sortedReports}
                loading={loading}
                onEditReport={handleEditReport}
              />
            </TabsContent>

            {/* Compliance Tab */}
            <TabsContent value="compliance" className="mt-0">
              <UserComplianceTracker
                reports={reports}
                userEmail={user?.email || ""}
                onCreateReport={(weekLabel) => {
                  setActiveTab("create");
                }}
              />
            </TabsContent>

            {/* Reminders Tab */}
            <TabsContent value="reminders" className="mt-0">
              <ReminderPreferencesPanel />
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>
    </div>
  );
};

export default Dashboard;
